import clsx from 'clsx'
import { css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { LinkTarget } from '@/common/types'
import { JSONAttributeParser } from '@/common/utils'
import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'
import { t } from '@/localization/localization'

import { FeedbackType } from '../feedback/feedback.component'
import styles from './file-input-list-item.scss?inline'

export type FileListElement = {
  name: string
  type: string
  size: number
  lastModified: number
}

/**
 * `dap-ds-file-input-list-item`
 * @summary A file input list item is a container for file input list items.
 * @element dap-ds-file-input-list-item
 * @title - File input list item
 * @group file-input
 *
 * @event {{ file: FileListElement }} dds-file-remove - Dispatched when a file is removed from the list.
 *
 * @csspart base - The main file input list item container.
 * @csspart feedback - The feedback container.
 * @csspart link - The link text of the item component.
 *
 * @cssproperty --dds-file-list-item-padding - The padding of the file list item.
 * @cssproperty --dds-file-list-item-border-width - The border width of the file list item.
 * @cssproperty --dds-file-list-item-border-color - The border color of the file list item.
 * @cssproperty --dds-file-list-item-border-radius - The border radius of the file list item.
 * @cssproperty --dds-file-list-item-bg-color - The background color of the file list item.
 * @cssproperty --dds-file-list-item-font-weight - The font weight of the file list item.
 * @cssproperty --dds-file-list-item-disabled-bg-color - The background color of the disabled file list item.
 * @cssproperty --dds-file-list-item-disabled-color - The text color of the disabled file list item.
 * @cssproperty --dds-file-list-item-content-margin - The margin of the content within the file list item.
 * @cssproperty --dds-file-list-item-content-gap - The gap between content elements in the file list item.
 * @cssproperty --dds-file-list-item-filename-color - The color of the filename text.
 * @cssproperty --dds-file-list-item-filesize-color - The color of the filesize text.
 * @cssproperty --dds-file-list-item-feedback-padding - The padding of the feedback section.
 * @cssproperty --dds-file-list-item-feedback-border-color - The border color of the feedback section.
 * @cssproperty --dds-file-list-item-feedback-font-size - The font size of the feedback text.
 */
@customElement('dap-ds-file-input-list-item')
export default class DapDSFileInputListItem extends SizedMixin(DdsElement) {
  /** Disables the file input list item. */
  @property({ type: Boolean, reflect: true }) disabled = false

  /** The name of the file input. */
  @property({ type: String }) inputName: string

  /** The feedback message. */
  @property({ type: String }) feedback: string

  /** Feedback type.
   * @type {'negative' | 'positive' | 'warning' | 'info'}
   */
  @property({ type: String }) feedbackType: FeedbackType

  /** Loading state of the file input list item. */
  @property({ type: Boolean, reflect: true }) loading? = false

  /** Show delete button. */
  @property({ type: String }) showDeleteButton: string = 'true'

  /** Show file size. */
  @property({ type: Boolean }) showSize? = false
  @property({ type: Object, converter: JSONAttributeParser })
  fileData: FileListElement

  /** Subtle style. */
  @property({ type: Boolean }) subtle = false

  /** The progress of the file upload. */
  @property({ type: String }) progress = '0'

  /** The url of the item link. If present, the item title will rendered as a link. */
  @property({ type: String }) href: string

  /** The rel of the item link. Only used if href is present. */
  @property({ type: String }) rel: string

  /** The target of the item link. Only used if href is present. */
  @property({ type: String }) target: LinkTarget

  /** Whether the file should be downloaded when clicked. */
  @property({ type: String }) download: string = 'true'

  static readonly styles = css`
    ${unsafeCSS(styles)}
  `

  delete() {
    this.emit('dds-file-remove', { file: this.fileData })
  }

  get formattedSize() {
    const exponent = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const fractionDigits = 0
    const size = this.fileData.size

    if (!+size) {
      return `0 ${sizes[0]}`
    }

    const i = Math.floor(Math.log(size) / Math.log(exponent))

    return `${parseFloat((size / Math.pow(exponent, i)).toFixed(fractionDigits))} ${sizes[i]}`
  }

  render() {
    return (
      this.fileData &&
      html`
        <li
          part="base"
          class=${clsx('file-list-item', `file-list-item--${this.size}`, {
            'file-list-item--disabled': this.disabled,
          })}
          role="status"
          aria-live="polite"
          aria-label=${`${this.fileData.name} ${this.loading ? t('fileInput.uploading') : ''} ${this.feedback ? this.feedback : ''}`}>
          <div class="content">
            <span class="content__filename">
              ${this.href
                ? html`<dap-ds-link
                    part="link"
                    href="${this.href}"
                    target=${ifDefined(this.target)}
                    rel=${ifDefined(this.rel)}
                    ?download=${this.download === 'true' ? true : undefined}
                    >${this.fileData.name}</dap-ds-link
                  >`
                : html`${this.fileData.name}`}
            </span>
            ${this.showSize
              ? html`<span
                  class="content__filesize"
                  aria-label=${t('fileInput.fileSize', {
                    size: this.formattedSize,
                  })}
                  >${this.formattedSize}</span
                >`
              : nothing}
            <span class="content__markers">
              ${this.loading
                ? html`<dap-ds-spinner
                    size="sm"
                    type="neutral"
                    aria-label=${t('fileInput.uploading')}></dap-ds-spinner>`
                : nothing}
              ${this.showDeleteButton === 'true'
                ? html`<dap-ds-icon-button
                    class="content__remove-button"
                    ?disabled="${this.disabled}"
                    size="lg"
                    arial-label=${`${t('fileInput.delete')} ${this.fileData.name}`}
                    clean
                    @click=${this.delete}>
                    <dap-ds-icon
                      class="content__remove-button-icon"
                      name="close-line"></dap-ds-icon>
                  </dap-ds-icon-button>`
                : nothing}
            </span>
          </div>
          ${this.feedback &&
          html`
            <dap-ds-feedback
              part="feedback"
              class="feedback"
              feedbackSize="sm"
              feedbackType=${ifDefined(this.feedbackType)}
              .subtle=${this.subtle}
              role="alert"
              aria-live="assertive">
              <span class="feedback__text">${this.feedback}</span>
            </dap-ds-feedback>
          `}
        </li>
      `
    )
  }
}
